import { AT_MENTION_ENTITIES } from '@/config/static/mentions';
import { PROVISIONED_USERS_CHANGED_EVENT } from '@/utils/constants';
import { toMentionEntities } from '@/services/userRegistry';

/**
 * Internal space inside a committed @-mention (not a token terminator).
 * Renders like a normal space; ASCII whitespace still ends the mention.
 */
export const MENTION_INTERNAL_SPACE = '\u00A0';

/** @-token: from `@` through internal NBSP segments, until ASCII whitespace. */
export const AT_TOKEN_PATTERN = new RegExp(
  `@[^\\s@]+(?:${MENTION_INTERNAL_SPACE}[^\\s@]+)*`,
  'g'
);

let cachedEntityNames: string[] | null = null;

function getMentionEntityNamesSorted(): string[] {
  if (!cachedEntityNames) {
    const users = toMentionEntities();
    const other = Object.entries(AT_MENTION_ENTITIES)
      .filter(([key]) => key !== 'users')
      .flatMap(([, list]) => list);
    cachedEntityNames = [...users, ...other]
      .map((e) => e.name)
      .sort((a, b) => b.length - a.length);
  }
  return cachedEntityNames;
}

export function invalidateMentionEntityNamesCache() {
  cachedEntityNames = null;
}

if (typeof window !== 'undefined') {
  window.addEventListener(PROVISIONED_USERS_CHANGED_EVENT, invalidateMentionEntityNamesCache);
}

/** Spaces in picker names → NBSP so the full label stays one purple token. */
export function formatMentionToken(name: string): string {
  return name.replace(/ /g, MENTION_INTERNAL_SPACE);
}

/** Restore normal spaces when sending or persisting message text. */
export function normalizeMentionText(text: string): string {
  return text.replace(/\u00A0/g, ' ');
}

/** Active `@` while typing (picker query — no spaces in partial token). */
export function getAtContext(
  value: string,
  cursorPos: number = value.length
): { atIndex: number; query: string } | null {
  const before = value.slice(0, cursorPos);
  const match = before.match(/@([\w./#-]*)$/);
  if (!match) return null;
  const atIndex = before.lastIndexOf('@');
  return { atIndex, query: match[1] ?? '' };
}

export function insertAtMention(
  text: string,
  cursorPos: number,
  token: string
): { value: string; cursorPos: number } {
  const formatted = formatMentionToken(token);
  const insert = `@${formatted} `;
  const ctx = getAtContext(text, cursorPos);
  if (!ctx) {
    const value = text.slice(0, cursorPos) + insert + text.slice(cursorPos);
    return { value, cursorPos: cursorPos + insert.length };
  }
  const value = text.slice(0, ctx.atIndex) + insert + text.slice(cursorPos);
  return { value, cursorPos: ctx.atIndex + insert.length };
}

export type MentionSpan = { start: number; end: number };

/**
 * Committed mention ranges: NBSP tokens + known entity names (legacy messages with regular spaces).
 */
export function scanMentionSpans(text: string): MentionSpan[] {
  const spans: MentionSpan[] = [];

  const re = new RegExp(AT_TOKEN_PATTERN.source, 'g');
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    spans.push({ start: match.index, end: match.index + match[0].length });
  }

  for (let i = 0; i < text.length; i++) {
    if (text[i] !== '@') continue;
    for (const name of getMentionEntityNamesSorted()) {
      if (!text.startsWith(name, i + 1)) continue;
      const end = i + 1 + name.length;
      if (end < text.length && !/\s/.test(text[end]!)) continue;
      if (spans.some((s) => i < s.end && end > s.start)) continue;
      spans.push({ start: i, end });
      break;
    }
  }

  spans.sort((a, b) => a.start - b.start);
  return spans;
}
